import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  UserPlus, 
  Search, 
  Settings, 
  BarChart3, 
  Calendar,
  Menu,
  X,
  Home,
  LogOut,
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  User,
  Loader2,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { memberService, postService, dashboardService } from '../services/apiService';
import RegistrationForm from './RegistrationForm';

const Dashboard = () => {
  const { user, isAdmin, logout } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);
  
  // Estados para postagens
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postFormData, setPostFormData] = useState({
    title: '',
    content: '',
    imageUrl: '',
    isPublished: true
  });
  
  // Estados para membros
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Menu items baseado no tipo de usuário
  const getMenuItems = () => {
    const baseItems = [
      { id: 'dashboard', label: 'Dashboard', icon: Home },
      { id: 'posts', label: 'Postagens', icon: FileText },
    ];

    if (isAdmin()) {
      baseItems.push(
        { id: 'cadastro', label: 'Novo Cadastro', icon: UserPlus },
        { id: 'membros', label: 'Membros', icon: Users },
        { id: 'configuracoes', label: 'Configurações', icon: Settings },
      );
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Recarregar membros quando navegar para a página de membros
  useEffect(() => {
    if (currentView === 'membros' && isAdmin()) {
      const loadMembers = async () => {
        setLoading(true);
        try {
          const membersResult = await memberService.getAll();
          if (membersResult.success) {
            console.log('Dados dos membros:', membersResult.data);
            setMembers(membersResult.data.members || membersResult.data);
          }
        } catch (error) {
          console.error('Erro ao carregar membros:', error);
        } finally {
          setLoading(false);
        }
      };
      loadMembers();
    }
  }, [currentView, isAdmin]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Carregar postagens (disponível para todos)
      const postsResult = await postService.getAll();
      if (postsResult.success) {
        setPosts(postsResult.data);
      }

      // Carregar estatísticas e membros (apenas para admin)
      if (isAdmin()) {
        const statsResult = await dashboardService.getMemberStats();
        if (statsResult.success) {
          console.log('Dados das estatísticas:', statsResult.data);
          setStats(statsResult.data);
        }

        const membersResult = await memberService.getAll();
        if (membersResult.success) {
          console.log('Dados dos membros:', membersResult.data);
          setMembers(membersResult.data.members || membersResult.data);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  // Funções para postagens
  const handleCreatePost = async () => {
    setLoading(true);
    try {
      const result = await postService.create(postFormData);
      if (result.success) {
        alert('Postagem criada com sucesso!');
        setShowPostForm(false);
        setPostFormData({ title: '', content: '', imageUrl: '', isPublished: true });
        loadInitialData(); // Recarregar postagens
      } else {
        alert('Erro ao criar postagem: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao criar postagem:', error);
      alert('Erro ao criar postagem');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    
    setLoading(true);
    try {
      const result = await postService.update(editingPost.id, postFormData);
      if (result.success) {
        alert('Postagem atualizada com sucesso!');
        setShowPostForm(false);
        setEditingPost(null);
        setPostFormData({ title: '', content: '', imageUrl: '', isPublished: true });
        loadInitialData();
      } else {
        alert('Erro ao atualizar postagem: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao atualizar postagem:', error);
      alert('Erro ao atualizar postagem');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Tem certeza que deseja excluir esta postagem?')) return;
    
    setLoading(true);
    try {
      const result = await postService.delete(postId);
      if (result.success) {
        alert('Postagem excluída com sucesso!');
        loadInitialData();
      } else {
        alert('Erro ao excluir postagem: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao excluir postagem:', error);
      alert('Erro ao excluir postagem');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostFormData({
      title: post.title,
      content: post.content,
      imageUrl: post.imageUrl || '',
      isPublished: post.isPublished
    });
    setShowPostForm(true);
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setPostFormData({ title: '', content: '', imageUrl: '', isPublished: true });
    setShowPostForm(true);
  };

  // Funções para membros
  const handleViewMember = async (memberId) => {
    setLoading(true);
    try {
      const result = await memberService.getById(memberId);
      if (result.success) {
        setSelectedMember(result.data);
        setShowMemberModal(true);
      } else {
        alert('Erro ao carregar dados do membro: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao carregar membro:', error);
      alert('Erro ao carregar dados do membro');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setShowMemberModal(true);
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Tem certeza que deseja desativar este membro?')) return;
    
    setLoading(true);
    try {
      const result = await memberService.delete(memberId);
      if (result.success) {
        alert('Membro desativado com sucesso!');
        loadInitialData();
      } else {
        alert('Erro ao desativar membro: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao desativar membro:', error);
      alert('Erro ao desativar membro');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMembers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.searchTerm = searchTerm;
      if (selectedCategory) params.category = selectedCategory;
      
      const result = await memberService.search(params);
      if (result.success) {
        setMembers(result.data);
      } else {
        alert('Erro na busca: ' + result.error);
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      alert('Erro ao buscar membros');
    } finally {
      setLoading(false);
    }
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const renderDashboardContent = () => (
    <div className="space-y-6">
      {isAdmin() && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Membros</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.totalMembers || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Membros Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.membersByCategory?.membro_ativo || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Frequentadores</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.membersByCategory?.frequentador || 0}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Users className="text-yellow-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Membros Afastados</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.membersByCategory?.membro_afastado || 0}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <Users className="text-red-600" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Postagens Recentes</h3>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-primary-600" size={24} />
            </div>
          ) : posts.length > 0 ? (
            <div className="space-y-4">
              {posts.slice(0, 5).map((post) => (
                <div key={post.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{post.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {post.content.substring(0, 100)}...
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Por {post.author?.fullName} • {new Date(post.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhuma postagem encontrada</p>
          )}
        </div>

        {isAdmin() && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividades Recentes</h3>
            <div className="space-y-4">
              {stats?.recentRegistrations && stats.recentRegistrations.length > 0 ? (
                stats.recentRegistrations.map((member, index) => (
                  <div key={member.id} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Novo membro cadastrado</p>
                      <p className="text-xs text-gray-500">
                        {member.fullName} - {new Date(member.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">Nenhuma atividade recente</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderMembersList = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Membros</h2>
        <button
          onClick={() => setCurrentView('cadastro')}
          className="btn-primary"
        >
          <Plus size={16} className="mr-2" />
          Novo Membro
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nome, email..."
              className="input-field"
            />
          </div>
          <div>
            <label className="label">Categoria</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field"
            >
              <option value="">Todas</option>
              <option value="frequentador">Frequentador</option>
              <option value="membro_afastado">Membro Afastado</option>
              <option value="membro_ativo">Membro Ativo</option>
            </select>
          </div>
          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearchMembers}
              className="btn-primary flex-1"
            >
              Buscar
            </button>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                loadInitialData();
              }}
              className="btn-secondary flex-1"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Lista de membros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-primary-600" size={32} />
          </div>
        ) : members.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum membro encontrado</h3>
              <p className="text-gray-600">Total de membros: {members.length}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Membro
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cargo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <User className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        member.category === 'membro_ativo' ? 'bg-green-100 text-green-800' :
                        member.category === 'frequentador' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {member.category === 'membro_ativo' ? 'Ativo' :
                         member.category === 'frequentador' ? 'Frequentador' : 'Afastado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.role || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.phone1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        member.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {member.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button 
                        onClick={() => handleViewMember(member.id)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleEditMember(member)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDeleteMember(member.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPostsList = () => (
    <div className="space-y-6">
      {showPostForm ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingPost ? 'Editar Postagem' : 'Nova Postagem'}
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="label">Título *</label>
              <input
                type="text"
                value={postFormData.title}
                onChange={(e) => setPostFormData({...postFormData, title: e.target.value})}
                placeholder="Digite o título da postagem"
                className="input-field"
                maxLength={255}
              />
            </div>
            
            <div>
              <label className="label">Conteúdo *</label>
              <textarea
                value={postFormData.content}
                onChange={(e) => setPostFormData({...postFormData, content: e.target.value})}
                placeholder="Digite o conteúdo da postagem"
                className="input-field"
                rows={6}
              />
            </div>
            
            <div>
              <label className="label">URL da Imagem (opcional)</label>
              <input
                type="url"
                value={postFormData.imageUrl}
                onChange={(e) => setPostFormData({...postFormData, imageUrl: e.target.value})}
                placeholder="https://example.com/image.jpg"
                className="input-field"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublished"
                checked={postFormData.isPublished}
                onChange={(e) => setPostFormData({...postFormData, isPublished: e.target.checked})}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isPublished" className="ml-2 text-sm text-gray-700">
                Publicar imediatamente
              </label>
            </div>
            
            <div className="flex space-x-3 pt-4">
              <button
                onClick={editingPost ? handleUpdatePost : handleCreatePost}
                disabled={loading || !postFormData.title || !postFormData.content}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  editingPost ? 'Atualizar Postagem' : 'Criar Postagem'
                )}
              </button>
              <button
                onClick={() => {
                  setShowPostForm(false);
                  setEditingPost(null);
                  setPostFormData({ title: '', content: '', imageUrl: '', isPublished: true });
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
                    <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Postagens</h2>
            {isAdmin() && (
              <button
                onClick={handleNewPost}
                className="btn-primary"
              >
                <Plus size={16} className="mr-2" />
                Nova Postagem
              </button>
            )}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-primary-600" size={32} />
          </div>
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {post.imageUrl && (
                <img
                  src={post.imageUrl}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {post.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {post.content.substring(0, 150)}...
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Por {post.author?.fullName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(post.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    post.isPublished ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {post.isPublished ? 'Publicada' : 'Rascunho'}
                  </span>
                  <div className="flex space-x-2">
                    <button className="text-primary-600 hover:text-primary-900">
                      <Eye size={16} />
                    </button>
                    {isAdmin() && (
                      <>
                        <button 
                          onClick={() => handleEditPost(post)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeletePost(post.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500">Nenhuma postagem encontrada</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettingsContent = () => (
    <div className="space-y-6">
      {/* Perfil do Usuário */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Meu Perfil</h3>
        </div>
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xl font-medium">
                {user?.firstName?.charAt(0) || 'U'}
              </span>
            </div>
            <div>
              <h4 className="text-lg font-medium text-gray-900">{user?.fullName}</h4>
              <p className="text-gray-600">{user?.email}</p>
              <p className="text-sm text-gray-500">{isAdmin() ? 'Administrador' : 'Membro'}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Nome</label>
              <input
                type="text"
                value={user?.firstName || ''}
                className="input-field"
                readOnly
              />
            </div>
            <div>
              <label className="label">Sobrenome</label>
              <input
                type="text"
                value={user?.lastName || ''}
                className="input-field"
                readOnly
              />
            </div>
            <div>
              <label className="label">E-mail</label>
              <input
                type="email"
                value={user?.email || ''}
                className="input-field"
                readOnly
              />
            </div>
            <div>
              <label className="label">Telefone</label>
              <input
                type="text"
                value={user?.phone1 || ''}
                className="input-field"
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Configurações da Conta */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Configurações da Conta</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-2">Alterar Senha</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="password"
                placeholder="Senha atual"
                className="input-field"
              />
              <input
                type="password"
                placeholder="Nova senha"
                className="input-field"
              />
              <button className="btn-primary">
                Alterar Senha
              </button>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-2">Preferências</h4>
            <div className="space-y-3">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span className="ml-2 text-sm text-gray-700">Receber notificações por e-mail</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span className="ml-2 text-sm text-gray-700">Mostrar informações pessoais no perfil</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Ações da Conta */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Ações da Conta</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
            >
              <LogOut size={16} className="mr-2" />
              Sair da Conta
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (currentView) {
      case 'cadastro':
        return <RegistrationForm />;
      case 'membros':
        return renderMembersList();
      case 'posts':
        return renderPostsList();
      case 'configuracoes':
        return renderSettingsContent();
      case 'dashboard':
      default:
        return renderDashboardContent();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Centro Espirita Fonte de Luz</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentView(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                    ${currentView === item.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon size={20} className="mr-3" />
                  {item.label}
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <Menu size={20} />
            </button>

            <div className="flex items-center space-x-4">
              {/* User Dropdown */}
              <div className="relative" ref={userDropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-3 p-2 rounded-md hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.firstName?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                    <p className="text-xs text-gray-500">{isAdmin() ? 'Administrador' : 'Membro'}</p>
                  </div>
                  <ChevronDown size={16} className="text-gray-400" />
                </button>

                {/* Dropdown Menu */}
                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      
                      <button
                        onClick={() => {
                          setCurrentView('configuracoes');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <User size={16} className="mr-3" />
                        Meu Perfil
                      </button>
                      
                      <button
                        onClick={() => {
                          setCurrentView('configuracoes');
                          setUserDropdownOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <Settings size={16} className="mr-3" />
                        Configurações
                      </button>
                      
                      <div className="border-t border-gray-100">
                        <button
                          onClick={() => {
                            handleLogout();
                            setUserDropdownOpen(false);
                          }}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut size={16} className="mr-3" />
                          Sair
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <main className="p-6">
          {currentView === 'cadastro' ? (
            renderContent()
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {currentView === 'dashboard' ? 'Dashboard' :
                   currentView === 'membros' ? 'Membros' :
                   currentView === 'posts' ? 'Postagens' :
                   currentView === 'configuracoes' ? 'Configurações' : 'Dashboard'}
                </h2>
                <p className="text-gray-600">
                  {currentView === 'dashboard' ? 'Visão geral da Centro Espirita Fonte de Luz' :
                   currentView === 'membros' ? 'Gerencie os membros da casa' :
                   currentView === 'posts' ? 'Visualize e gerencie as postagens' :
                   'Navegue pelas funcionalidades do sistema'}
                </p>
              </div>
              {renderContent()}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
